import { useState, ReactNode, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ProductFruits, useProductFruitsApi } from "react-product-fruits";
// import { Crisp } from "crisp-sdk-web";
// import useNativeAppStyling from "hooks/useNativeAppStyling";

// material-ui
import {
  Box,
  Alert,
  Divider,
  Step,
  Stepper,
  StepLabel,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Project imports
import Welcome from "./welcome";
import { getOrganizationSetupError } from "./services/organizationSetupError";
import LearnAboutUs, { LearnAboutUsAnswer } from "./learnAboutUs";
import CreateOrg, { BusinessRegNo } from "./createOrg";
import Questionnaire, { QuestionnaireAnswers } from "./questionnaire";
import Button from "components/@extended/LoadingButton";
import useAuth from "hooks/useAuth";
import { useTranslation } from "utils/locales/utilityFunctions";
import { OnboardingObject } from "types/auth";
import useStateRef from "react-usestateref";
import useUserActivity from "hooks/useUserActivity";
import { googlePlacesAutocompleteLoaded } from "utils/scriptLoader";
import config from "config/config";
import { ERROR_REPORTING_EMAIL } from "config/config-general";
import useConfig from "hooks/useConfig";
import { getProductFruitsTourUrl } from "config/servicesConfig/productFruits";
import { schedulePendingWelcome } from "features/aiChat/utils/georgeOnboarding";
import { getInternalAppType } from "utils/brandingUtils";
import { openDrawer } from "store/reducers/menu";
import mapLocationPathToPageView from "services/mixpanel/mapLocationPathToPageView";
// import { useDebugLog } from "hooks/useDebugLog";
// import DebugOverlay from "components/debug/debugOverlay";

// --- Get step content logic ---//
const getStepContent = (
  step: number,
  handleNext: () => Promise<void>,
  handleBack: () => void,
  setErrorIndex: (i: number | null) => void,

  learnAboutUsAnswer: LearnAboutUsAnswer,
  setLearnAboutUsAnswer: (d: LearnAboutUsAnswer) => void,

  businessRegNo: BusinessRegNo,
  setBusinessRegNo: (d: BusinessRegNo) => void,

  questionnaireAnswers: QuestionnaireAnswers,
  setQuestionnaireAnswers: (values: QuestionnaireAnswers) => void,
  disableBackButton?: boolean,
  userOnHoldBusinessExists?: boolean,
) => {
  switch (step) {
    case 0:
      return <Welcome />;
    case 1:
      return (
        <LearnAboutUs
          handleNext={handleNext}
          handleBack={handleBack}
          disableBackButton={disableBackButton}
          setErrorIndex={setErrorIndex}
          learnAboutUsAnswer={learnAboutUsAnswer}
          setLearnAboutUsAnswer={setLearnAboutUsAnswer}
        />
      );
    case 2:
      if (!googlePlacesAutocompleteLoaded) return null;
      return (
        <CreateOrg
          handleNext={handleNext}
          handleBack={handleBack}
          disableBackButton={disableBackButton}
          setErrorIndex={setErrorIndex}
          businessRegNo={businessRegNo}
          setBusinessRegNo={setBusinessRegNo}
          userOnHoldBusinessExists={userOnHoldBusinessExists}
        />
      );
    case 3:
      return (
        <Questionnaire
          handleNext={handleNext}
          handleBack={handleBack}
          disableBackButton={disableBackButton}
          setErrorIndex={setErrorIndex}
          questionnaireAnswers={questionnaireAnswers}
          setQuestionnaireAnswers={setQuestionnaireAnswers}
        />
      );
    default:
      throw new Error("Unknown step");
  }
};

// ==============================|| ONBOARDING WIZARD ||============================== //

const Onboarding = () => {
  // --- Hooks --- //
  const dispatch = useDispatch();
  const {
    user,
    userProfileLoadedFromServer,
    userOnboardingObject,
    userOrg,
    setAIAssistantData,
    updateUserOnboardingObject,
    updateUserSettings,
    updateOrganization,
    updateProfile,
    sendEmail,
    callCloudFunction,
  } = useAuth();
  // const { logs, addLog, clearLogs} = useDebugLog();
  const { i18n } = useConfig();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const matchDownMD = useMediaQuery(theme.breakpoints.down("md"));
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen = matchDownLG;
  // const { chatModalOffset, chatButtonOffset } = useNativeAppStyling();

  useUserActivity({ user: user, updateUserSettings });
  // Set up onboarding step titles
  const steps = [
    { id: "welcome", label: translate("onboarding-steps-title-welcome") },
    { id: "about-us", label: translate("onboarding-steps-title-about-us") },
    { id: "company", label: translate("onboarding-steps-title-company") },
    { id: "needs", label: translate("onboarding-steps-title-your-needs") },
  ];

  // --- States and references --- //
  const disableBackStep = useRef<number>(0);
  const userOnHoldBusinessExists = useRef(false);
  const userPFRef = useRef<any | null>(null);
  const pfTourFinishedCallback = useRef<any | null>(null);
  const [loadingTimerState, setLoadingTimerState] = useState(true);
  const [showProductFruits, setShowProductFruits] = useState(true);

  const [activeStep, setActiveStep] = useState(0);
  const [learnAboutUsAnswer, setLearnAboutUsAnswer, learnAboutUsAnswerRef] =
    useStateRef<LearnAboutUsAnswer>({ answer: "" });
  const [businessRegNo, setBusinessRegNo, businessRegNoRef] = useStateRef<BusinessRegNo>({
    businessNo: "",
    data: null,
    businessExists: false,
  });
  const [questionnaireAnswers, setQuestionnaireAnswers, questionnaireAnswersRef] =
    useStateRef<QuestionnaireAnswers>({
      role: "",
      estates: "",
      hectares: "",
      purposes: [],
      roleId: "",
      estatesId: "",
      hectaresId: "",
      purposesIds: [],
    });
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [setupError, setSetupError] = useState<ReturnType<typeof getOrganizationSetupError> | null>(null);
  const [recoveringOrganization, setRecoveringOrganization] = useState(false);
  const missingOrganization = !!user && user.authType !== "anonymous" && !user.orgId;
  const needsOnboarding = user?.onboarded === false || missingOrganization || recoveringOrganization;
  const [errorIndex, setErrorIndex] = useState<number | null>(null);

  // --- Effects --- //
  // Report page views to mixpanel
  useEffect(() => {
    if (location.pathname) {
      const page = mapLocationPathToPageView(location.pathname);
      callCloudFunction("trackEvent", { event: page });
    }
  }, [location.pathname]);

  // Product fruits initialization
  useEffect(() => {
    // Toggle the rendering state to force reinitialization
    setShowProductFruits(false);
    const timer = setTimeout(() => {
      setShowProductFruits(true);
    }, 100); // Short delay to ensure proper unmounting and remounting
    // Cleanup function to clear the timer
    return () => {
      clearTimeout(timer);
      if (pfTourFinishedCallback.current) {
        pfTourFinishedCallback.current();
        pfTourFinishedCallback.current = null;
      }
    };
  }, [i18n]);

  useEffect(() => {
    if (user && !userPFRef.current) {
      userPFRef.current = {
        username: user.id, // REQUIRED - any unique user identifier
        email: user.email,
        firstname: user.firstName,
        lastname: user.lastName,
        signUpAt: user.creationDate,
        role: user.orgRole,
        // props: { customProp1: '==REPLACE==' }
      };
    }
  }, [user]);

  // Set loading timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTimerState(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (userProfileLoadedFromServer && missingOrganization && user?.onboarded === true) {
      setRecoveringOrganization(true);
    }
    if (savingRef.current) return;
    if (userOnboardingObject) {
      // A saved questionnaire/final step does not prove that org creation succeeded.
      const savedStep = Number(userOnboardingObject.step) || 0;
      const resumeStep = missingOrganization ? Math.min(savedStep, 2) : savedStep;
      setActiveStep(resumeStep);
      disableBackStep.current = resumeStep;
      userOnHoldBusinessExists.current = !!userOnboardingObject.businessRegNoAndName?.businessExists;
      if (userOnboardingObject.learnAboutUsAnswer) setLearnAboutUsAnswer(userOnboardingObject.learnAboutUsAnswer);
      if (userOnboardingObject.businessRegNoAndName) {
        const business = userOnboardingObject.businessRegNoAndName;
        setBusinessRegNo({ businessNo: business.regNo, data: { name: business.name }, businessExists: business.businessExists });
      }
      if (userOnboardingObject.questionaireAnswers) setQuestionnaireAnswers(userOnboardingObject.questionaireAnswers);
    } else if (missingOrganization && user?.onboarded === true) {
      setActiveStep(2);
    }
  }, [userOnboardingObject, user?.id, missingOrganization, userProfileLoadedFromServer]);

  // --- Product fruits functionality --- //
  useProductFruitsApi(
    (api) => {
      // Listen for tour finished event
      if (!pfTourFinishedCallback.current) {
        pfTourFinishedCallback.current = api.tours.listen("tour-finished", (tourId: number) => {
          const pfTours = api.tours.getTours();
          const pfTourCompleted = pfTours.find((t: any) => t.id === tourId);
          // Check for pre-tour type and start tour with the same name
          if (!pfTourCompleted || !pfTourCompleted.name.startsWith("pre-tour")) return;
          // Find tour name by splitting pre-tour name
          const pfTourName = pfTourCompleted.name.split("_")[1];
          // Find Tour id by name
          const pfTour = api.tours.getTours().find((tour: any) => tour.name === pfTourName);
          // Start the next tour
          if (pfTour && pfTour.id) {
            api.tours.tryStartTour(pfTour.id);
            if (isSmallScreen) {
              dispatch(openDrawer({ drawerOpen: false }));
            }
          } else {
            // Send email to user that the tour can not be found
            sendEmail(
              ERROR_REPORTING_EMAIL,
              "[Error PF] Product Fruits tour not found",
              `Tour ${pfTourName} not found`,
            );
          }
        });
      }
      // Only have chat on the home-page
      const isHomePage = location.pathname === "/home-page" || location.pathname === "/";
      if (!isHomePage) api.button.close();
    },

    [location],
  );

  // --- Event handlers --- //
  const handleNext = async () => {
    if (!user?.id || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setSetupError(null);
    try {
      const curStep = activeStep + 1;
      let data: OnboardingObject = { step: curStep };
      if (steps[activeStep].id === "about-us") {
        data.learnAboutUsAnswer = { answer: learnAboutUsAnswerRef.current.answer || "" };
      } else if (steps[activeStep].id === "company") {
        const business = businessRegNoRef.current;
        data.businessRegNoAndName = {
          regNo: business.businessNo || "",
          name: business.data?.name || "",
          businessExists: business.businessExists,
        };
        if (business.businessExists) {
          userOnHoldBusinessExists.current = true;
          await updateUserSettings(user.id, { deletable: true });
          await updateUserOnboardingObject(user.id, { ...data, step: activeStep });
          return;
        }
        const bizData = business.data || {};
        // Do not persist the next step until the org, membership and init succeed.
        await updateOrganization(null, {
          id: null,
          name: bizData.name || "",
          businessRegNo: business.businessNo,
          address: bizData.address || "",
          zipcode: bizData.zipcode || "",
          city: bizData.city || "",
          contacts: [],
        }, { ...bizData });
      } else if (steps[activeStep].id === "needs") {
        data.questionaireAnswers = { ...questionnaireAnswersRef.current };
      }
      await updateUserOnboardingObject(user.id, data);
      setActiveStep(curStep);
      setErrorIndex(null);
    } catch (error) {
      console.error("Onboarding step failed", error);
      setSetupError(getOrganizationSetupError(error));
      setErrorIndex(activeStep);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (!savingRef.current) setActiveStep(activeStep - 1);
  };

  const handleFinish = async () => {
    if (!user?.id || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setSetupError(null);
    try {
      const internalAppType = getInternalAppType({ pathOrHost: window.location.host, orgData: userOrg, userData: user });
      // The context verifies current Firestore membership and subscription in a transaction.
      // Never spread a stale profile over the backend's new orgId/roles.
      await updateProfile({ id: user.id, onboarded: true }, false);
      setRecoveringOrganization(false);
      if (internalAppType === "george") {
        schedulePendingWelcome();
        setAIAssistantData("openAiChatWindow", true);
      } else {
        navigate("/home-page", { replace: true });
        // navigate(userOrg?.subscription?.isOrgToOrgSubscription ? "/home-page" : getProductFruitsTourUrl("welcome"), { replace: true });
      }
      setLearnAboutUsAnswer({});
      setActiveStep(0);
    } catch (error) {
      console.error("Onboarding completion failed", error);
      setSetupError(getOrganizationSetupError(error));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  // --- Render --- //
  // Onboarding is only for a resolved profile which explicitly requires it.
  // A missing profile is a loading/error state, never an invitation to create
  // a replacement account.
  if (!user || user.authType === "anonymous" || !userProfileLoadedFromServer) return null;

  // --- Product fruits JSX --- //
  if (!loadingTimerState && !needsOnboarding && user && user.onboarded && userPFRef.current) {
    if (showProductFruits) {
      // Set language. If da set da, all other to english
      const languge = i18n === "da" ? "da" : "en";
      return (
        <ProductFruits
          workspaceCode={config.PF_WORKSPACE_CODE}
          language={languge}
          user={userPFRef.current}
          lifeCycle="unmount"
          config={{
            customNavigation: {
              use: true,
              navigate: (url) => {
                // Check if the URL is external (starts with http or https)
                if (/^https?:\/\//.test(url)) {
                  // Open external URLs in a new tab
                  window.open(url, "_blank", "noopener,noreferrer");
                } else {
                  // Otherwise, handle it as an internal URL
                  navigate(url);
                }
              },
              onGet() {
                // Always must start with https:// !!!
                return window.location.href;
              },
            },
          }}
        />
      );
    } else return null;
  } else if (!needsOnboarding) {
    return null;
  }
  // --- Onboarding JSX --- //
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 100,
        backgroundColor: "white",
      }}
    >
      <Dialog open={true} maxWidth="sm" sx={{ "& .MuiDialog-paper": { width: "100%" } }}>
        <Box sx={{ p: !matchDownMD ? 1 : 0 }}>
          <DialogTitle>{translate("onboarding-main-container-title")}</DialogTitle>

          <Divider />

          <DialogContent>
            {/* STEPPER */}
            <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 4 }}>
              {steps.map((step, index) => {
                const labelProps: { error?: boolean; optional?: ReactNode } = {};

                if (index === errorIndex) {
                  labelProps.error = true;
                }

                return (
                  <Step key={step.label}>
                    {index === activeStep ? (
                      <StepLabel {...labelProps}>{step.label}</StepLabel>
                    ) : (
                      <StepLabel {...labelProps}>{`${step.label.substring(0, 5)} ...`}</StepLabel>
                    )}
                  </Step>
                );
              })}
            </Stepper>

            <Divider />
          </DialogContent>

          {setupError && <DialogContent><Alert severity="error">{translate(setupError.messageKey, {
            fields: setupError.fieldKeys?.map((key) => translate(key)).join(", "),
          })}</Alert></DialogContent>}

          {/* CONTENT */}
          {activeStep === steps.length ? (
            <>
              {/* LAST PAGE */}
              <DialogContent>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {translate("onboarding-final-title")}
                </Typography>

                <Typography variant="h6" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                  {translate("onboarding-final-suggestions")}
                </Typography>
              </DialogContent>

              <Divider />
            </>
          ) : (
            <>
              {getStepContent(
                activeStep,
                handleNext,
                handleBack,
                setErrorIndex,
                learnAboutUsAnswer,
                setLearnAboutUsAnswer,
                businessRegNo,
                setBusinessRegNo,
                questionnaireAnswers,
                setQuestionnaireAnswers,
                isSaving || activeStep <= disableBackStep.current,
                userOnHoldBusinessExists.current,
              )}
            </>
          )}

          {/* ACTION */}
          {activeStep === steps.length ? (
            <>
              {/* LAST PAGE */}
              <DialogActions>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ width: 1, pl: 1.5, pr: 1.5, py: 0.75 }}
                >
                  <Button
                    variant="contained"
                    loading={isSaving}
                    disabled={isSaving}
                    onClick={handleFinish}
                    sx={{ bgcolor: "primary.light" }}
                  >
                    {translate("get-started")}
                  </Button>
                </Stack>
              </DialogActions>
            </>
          ) : (
            <>
              {/* FIRST PAGE AND SECOND TO LAST PAGE */}
              {(activeStep === steps.length || activeStep === 0) && (
                <DialogActions>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent={activeStep !== 0 ? "space-between" : "flex-end"}
                    sx={{ width: 1, pl: 1.5, pr: 1.5, py: 0.75 }}
                  >
                    {activeStep !== 0 && activeStep > disableBackStep.current && (
                      <Button color="secondary" disabled={isSaving} onClick={handleBack}>
                        {translate("back")}
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      loading={isSaving}
                      disabled={isSaving}
                      onClick={handleNext}
                      sx={{ bgcolor: "primary.light" }}
                    >
                      {activeStep === steps.length - 1
                        ? translate("get-started")
                        : translate("next")}
                    </Button>
                  </Stack>
                </DialogActions>
              )}
            </>
          )}
        </Box>
      </Dialog>
    </div>
  );
};

export default Onboarding;
